// Red61 Ticketing related code

/*
    When the website visitor selects a given performance from the calendar, the website always needs to make a call
    to VIA (via the caching wrapper) to load the detailed pricing information, which also provides ticket availability
    information for each priceband.

    If all pricebands in this performance have the seats boolean set to false, the website should update the flag in
    the performance table to be false. If any of them return true, then the website should update its flag to true in
    it’s performance table.

    This will ensure that the performance calendar then shows the current data (as seen by the wrapper’s cache, which
    might be lagging behind the live ticket availability count).

    -- https://apidocs.red61.com/7.4.0-rc1/guides/performance_sync.html
 */

import { fetchForm, setFormSubmitButtonEnabled } from "./api";
import { Analytics } from "./analytics";
import {setAccordionCrossClasses} from "./accordion";

$(document).ready(function () {
    // increase/decrease ticket buttons
    const updateTicketPricingSpan = (input) => {
        const singularPrice = parseFloat(input.getAttribute('data-singular-price'));
        const priceSpan = document.querySelector(`span[data-input="${input.name}"]`)
        let count = parseInt(input.value);

        if (isNaN(count)) {
            count = 0;
        }

        if (priceSpan) {
            if (count) {
                priceSpan.innerText = (count * singularPrice).toFixed(2);
                priceSpan.parentElement.classList.remove('hidden');
            } else {
                priceSpan.parentElement.classList.add('hidden');
                priceSpan.innerText = '';
            }
        }

        updatePerformanceSelectionTotal(input.closest('form'));
    };

    const updatePerformanceSelectionTotal = (form) => {
        const performanceTicketsTotalEle = form.querySelector('span.order-total');
        const selectionInputs = form.querySelectorAll('input[data-singular-price]');

        if (performanceTicketsTotalEle) {
            let totalPrice = 0;
            let totalCount = 0;

            for (let input of selectionInputs) {
                const singularPrice = parseFloat(input.getAttribute('data-singular-price'));
                let count = parseInt(input.value);
                if (isNaN(count)) {
                    count = 0;
                }

                totalPrice += (count * singularPrice);
                totalCount += count;
            }

            performanceTicketsTotalEle.innerText = totalPrice.toFixed(2);
        }
    };

    // Retrieve whether we have viewed the ID requirements modal or not yet during this SESSION
    const getHasSeenIdRequirements = (eventId) => {
        const sessionStorage = window.sessionStorage;
        if (sessionStorage) {
            const eventPopup = JSON.parse(sessionStorage.getItem('eventPopup') ?? '{}');

            return eventPopup?.[eventId] === true;
        }

        return false;
    }

    // If we have viewed the ID requirements modal, persist this to SESSION storage
    const persistHasSeenIdRequirements = (eventId) => {
        const sessionStorage = window.sessionStorage;
        if (sessionStorage) {
            const eventPopup = JSON.parse(sessionStorage.getItem('eventPopup') ?? '{}');
            eventPopup[eventId] = true;

            sessionStorage.setItem('eventPopup', JSON.stringify(eventPopup));
        }
    }

    const doAddTicketsToBasket = (form) => {
        fetchForm(form).then((resp) => {
            Analytics.addBasketItems({
                items: resp.itemAdditions, amount:  resp.itemAdditions.reduce((carry, item) => carry + item.price, 0)
            });

            window.location.href = '/cart';
        }).catch((err) => {
            const errorEle = form.querySelector('.form-error');
            errorEle.innerText = err?.response?.error ?? '';

            setFormSubmitButtonEnabled(form, true);

            if (idModal) {
                idModal.classList.remove('!translate-y-0')
                idModal.classList.remove('!opacity-100');
                idModal.parentElement.classList.add('hidden');
            }
        });
    };

    // Check if our current page has the ID requirements modal, and if so, setup our click handler
    const idModal = document.getElementById('id-required-modal');

    let idModalForm  = null;
    if (idModal) {
        idModal.querySelectorAll('button[data-ticketing][name="accept"]').forEach((button) => {
            button.addEventListener('click', () => {
                button.setAttribute('disabled', 'true');
                persistHasSeenIdRequirements(idModal.getAttribute('data-event-id'));

                doAddTicketsToBasket(idModalForm);
            });
        });

        idModal.querySelectorAll('button[data-ticketing][name="close"]').forEach((button) => {
            button.addEventListener('click', () => {
                persistHasSeenIdRequirements(idModal.getAttribute('data-event-id'));

                idModal.classList.remove('!translate-y-0')
                idModal.classList.remove('!opacity-100');
                idModal.parentElement.classList.add('hidden');
            });
        });
    }

    // Purchase tickets forms
    document.querySelectorAll('form[data-ticketing][name="tickets-form"]').forEach((form) => {
        const quantityInputs = [...form.querySelectorAll('input[name^="qty"]')];
        const formEntryId = form.getAttribute('data-entry-id');

        form.addEventListener('change', (e) => {
            quantityInputs.forEach((ticketInput) => {
                const ticketError = form.querySelector(`.quantity-error[data-name="${ticketInput.name}"]`);
                ticketError.innerText = null;

                const val = ticketInput.value.length ? parseInt(ticketInput.value) : 0;
                const ticketsMax = parseInt(ticketInput.getAttribute('max'));
                const ticketsRemaining = parseInt(ticketInput.getAttribute('data-remaining'));
                const ticketStep = parseInt(ticketInput.step);

                if (val % ticketStep !== 0) {
                    ticketError.innerText = `Tickets must be purchased in multiples of ${ticketStep}`;
                } else if (val > ticketsMax) {
                    if (ticketsMax === ticketsRemaining && ticketsRemaining) {
                        ticketError.innerText = `There are only ${ticketsMax} ticket(s) of this type remaining`;
                    } else {
                        ticketError.innerText = `You can only purchase ${ticketsMax} tickets to a performance at a time`;
                    }
                }
            });
        });

        form.addEventListener('submit', (event) => {
            event.preventDefault();

            const form = event.target;
            const errorEle = form.querySelector('.form-error');
            errorEle.innerText = '';

            const changeCount = quantityInputs.reduce((changeCount, ticketInput) => {
                const val = ticketInput.value.length ? parseInt(ticketInput.value) : 0;
                const original = parseInt(ticketInput.getAttribute('data-original'));

                return changeCount + (val === original ? 0 : 1);
            }, 0);

            if (!changeCount) {
                errorEle.innerText = 'No tickets have been selected';

                return;
            }

            // IF our ID modal exists, and we HAVENT displayed it in this session
            // We are doing this AFTER the above logic, so that the 'no tickets selected' error still has a chance to be displayed first
            if (!getHasSeenIdRequirements(formEntryId) && idModal) {
                idModal.classList.add('!translate-y-0')
                idModal.classList.add('!opacity-100');
                idModal.parentElement.classList.remove('hidden');
                idModal.setAttribute('data-event-id',formEntryId);

                // Set the form to submit on when user confirms
                idModalForm = form;
                return;
            }

            doAddTicketsToBasket(form);
        });

        updatePerformanceSelectionTotal(form);
    });

    document.querySelectorAll('button[data-ticketing][name="reduce-tickets"]').forEach((button) => {
        const localValueInput = button.parentElement.querySelector('input');
        const step = parseInt(localValueInput.step);

        localValueInput.addEventListener('change', () => {
            updateTicketPricingSpan(localValueInput);
        });

        button.addEventListener('click', (e) => {
            let currVal = parseInt(localValueInput.value);
            const ticketsMax = parseInt(localValueInput.getAttribute('max'));

            if (isNaN(currVal)) {
                currVal = 0;
            }

            let newVal = Math.min(currVal-step, ticketsMax);
            newVal -= (newVal % step);

            localValueInput.value = Math.max(newVal, 0).toString();
            updateTicketPricingSpan(localValueInput);

            localValueInput.dispatchEvent(new Event('change'));
        });
    });

    document.querySelectorAll('button[data-ticketing][name="increase-tickets"]').forEach((button) => {
        const localValueInput = button.parentElement.querySelector('input');
        const step = parseInt(localValueInput.step);

        button.addEventListener('click', (e) => {
            let currVal = parseInt(localValueInput.value);
            const ticketsMax = parseInt(localValueInput.getAttribute('max'));

            if (isNaN(currVal)) {
                currVal = 0;
            }

            let newVal = Math.min(currVal+step, ticketsMax);
            newVal -= (newVal % step);

            if (newVal <= ticketsMax) {
                localValueInput.value = newVal.toString();
                updateTicketPricingSpan(localValueInput);

                localValueInput.dispatchEvent(new Event('change'));
            }
        });
    });

    document.querySelectorAll('div[data-ticketing].select-a-session').forEach((container) => {
        const sessionContainer = container.querySelector('div.select-session');
        const dateSelectors = sessionContainer.querySelectorAll('button[data-ticketing][data-date]');
        const performanceCards = container.querySelectorAll('div.performance-cards > div > button');
        const selectTicketsContainer = container.querySelector('div.performance-cards > div > div');
        const performanceAccordions = container.querySelectorAll('div.performance-cards > div');

        let selectedDateAnchor = [...dateSelectors].find((dateSelector) => dateSelector.classList.contains('selected'));
        let selectedPerformanceRef = null;

        dateSelectors.forEach((dateSelector) => {
            dateSelector.addEventListener('click', (event) => {
                for ( let dateSelector of dateSelectors ) {
                    dateSelector.classList.remove('selected');
                    dateSelector.ariaSelected = 'false';
                }
                dateSelector.classList.add('selected');
                dateSelector.ariaSelected = 'true';

                selectedDateAnchor = event.target;
                selectedPerformanceRef = null;

                updatePerformanceVisibility();
            });
        });

        const updatePerformanceVisibility = () => {
            const selectedDate = selectedDateAnchor.getAttribute('data-date');
            const datePerformanceCount = [...performanceAccordions].filter((performanceAccordion) => performanceAccordion.querySelector('button[data-date]').getAttribute('data-date') === selectedDate).length;

            performanceAccordions.forEach((performanceAccordion) => {
                const performanceCard = performanceAccordion.querySelector('button[data-date]');
                let accordionContent = performanceAccordion.querySelector('.accordion-content');

                if (performanceCard.getAttribute('data-date') === selectedDate) {
                    performanceAccordion.classList.remove('hidden');
                    performanceCard.classList.remove('hidden');

                    const displayPricebands = datePerformanceCount > 1;
                    if (!displayPricebands && accordionContent) {
                        setAccordionCrossClasses(performanceAccordion, true);
                        accordionContent.classList.remove('!max-h-[0]', 'hidden', 'opacity-0');
                    }
                } else {
                    performanceAccordion.classList.add('hidden');
                    performanceCard.classList.add('hidden');
                    setAccordionCrossClasses(performanceAccordion, false);

                    if (accordionContent) {
                        accordionContent.classList.add('!max-h-[0]', 'hidden', 'opacity-0');
                    }
                }
            });
        }
    });
});